import {
  ChildMixUpdate,
  MixCreate,
  MixRead,
  ProductionMixResultRead,
} from "src/store/api/generatedApi";
import {
  BasketLayerState,
  LayerItem,
  MaterialBalance,
  MaterialTotals,
  MixLayerState,
} from "./types";

// Ignore floating-point drift when comparing material totals.
export const MATERIAL_BALANCE_TOLERANCE = 1e-6;

export const mixReadToBasketLayerStates = (
  mix: MixRead
): BasketLayerState[] => {
  const fullnessByBasketNumber = new Map(
    (mix.summary?.basket_summaries ?? []).map(
      (basketSummary) =>
        [basketSummary.basket_number, basketSummary.fullness] as const
    )
  );
  return mix.baskets
    .map((basket): BasketLayerState => ({
      basketNumber: basket.basket_number,
      fullness: fullnessByBasketNumber.get(basket.basket_number) ?? null,
      layers: basket.layers.map((layer, index): LayerItem => ({
        id: `${mix.id}-${basket.basket_number}-${index}`,
        materialName: layer.material_name,
        weight: layer.quantity.value,
        unit: layer.quantity.unit,
      })),
    }))
    .sort((a, b) => a.basketNumber - b.basketNumber);
};

export const savedMixToLayerState = (
  result: ProductionMixResultRead
): MixLayerState => ({
  mixId: result.mix.id,
  productionGroupName: result.name,
  baskets: mixReadToBasketLayerStates(result.mix),
  parentMixIds: [result.mix.id],
  steelGradeProduction: result.production,
  description: result.description ?? undefined,
});

export const mixStateToMaterialTotals = (
  mix: Pick<MixLayerState, "baskets">
): MaterialTotals => {
  const totals: MaterialTotals = {};
  for (const basket of mix.baskets) {
    for (const layer of basket.layers) {
      totals[layer.materialName] =
        (totals[layer.materialName] ?? 0) + layer.weight;
    }
  }
  return totals;
};

export const computeMaterialBalances = (
  current: MaterialTotals,
  baseline: MaterialTotals,
  tolerance: number
): MaterialBalance[] => {
  const materialNames = Array.from(
    new Set([...Object.keys(baseline), ...Object.keys(current)])
  ).sort();
  return materialNames.map((materialName) => {
    const baselineValue = baseline[materialName] ?? 0;
    const currentValue = current[materialName] ?? 0;
    const delta = currentValue - baselineValue;
    return {
      materialName,
      baseline: baselineValue,
      current: currentValue,
      delta,
      isWithinTolerance: Math.abs(delta) <= tolerance,
    };
  });
};

export const stateToChildMixUpdates = (
  mixStates: MixLayerState[]
): ChildMixUpdate[] =>
  mixStates.map((mix) => ({
    parent_mix_ids: mix.parentMixIds,
    new_mix_production: {
      name: mix.productionGroupName,
      description: mix.description ?? null,
      production: mix.steelGradeProduction,
      mix: basketsToMixCreate(mix.baskets),
    },
  }));

export const basketsToMixCreate = (baskets: BasketLayerState[]): MixCreate => ({
  baskets: baskets.map((basket) => ({
    basket_number: basket.basketNumber,
    layers: basket.layers
      .filter((layer) => layer.weight > 0)
      .map((layer, index) => ({
        material_name: layer.materialName,
        quantity: { value: layer.weight, unit: layer.unit },
        layer_number: index + 1,
      })),
  })),
  thresholds: [],
});
